import isNil from 'lodash/isNil'
import {getSpotPriceTypeForTransaction} from '@/util/refinery'
import request from '@/util/request'
import { normalizeLotWeights, normalizeAssayWeights, normalizeWeightArray } from '@/util/refinery/weightFieldNormalizer'

// Constants

/**
 * Normalize rich weight fields in lot data to display numbers
 * This ensures all components receive numeric weights instead of rich weight objects
 */
function normalizeRichWeights(lot) {
  if (!lot) return lot
  
  // Normalize lot-level weights
  const normalized = normalizeLotWeights(lot, { preserveOriginal: true })
  
  // Normalize assays if present
  if (Array.isArray(normalized.assays)) {
    normalized.assays = normalizeWeightArray(normalized.assays, normalizeAssayWeights, { preserveOriginal: true })
  }
  
  return normalized
}

const ERROR_MESSAGES = {
  NO_COMPANY_SELECTED: 'No company selected',
  LOT_ID_REQUIRED: 'Lot ID is required',
  LOT_ID_PARAM_REQUIRED: 'lotId required',
  COMPANY_ID_REQUIRED: 'companyId required',
  TRACKING_NUMBERS_REQUIRED: 'trackingNumbers required',
  LOT_CODE_MISSING: 'lotCode missing for lot',
  RECORDS_ARRAY_REQUIRED: 'records array is required'
}

// Helper functions
const validateCompanySelected = (getters) => {
  const companyId = getters['getSelectedCompany']?.id
  if (!companyId) throw new Error(ERROR_MESSAGES.NO_COMPANY_SELECTED)
  return companyId
}

const validateLotId = (lotId) => {
  if (!lotId) throw new Error(ERROR_MESSAGES.LOT_ID_REQUIRED)
}

const validateLotIdParam = (lotId) => {
  if (!lotId) throw new Error(ERROR_MESSAGES.LOT_ID_PARAM_REQUIRED)
}

const buildSpotPriceSnapshot = (spotPrices) => {
  const priceType = getSpotPriceTypeForTransaction('spot_lock', 'sell')
  return {
    snapshotSpotPriceGold: parseFloat(spotPrices.Gold[priceType]),
    snapshotSpotPriceSilver: parseFloat(spotPrices.Silver[priceType]),
    snapshotSpotPricePlatinum: parseFloat(spotPrices.Platinum[priceType]),
    snapshotSpotPricePalladium: parseFloat(spotPrices.Palladium[priceType])
  }
}

const buildSubmissionPayload = (companyId, payload) => {
  return {
    companyId,
    refineryContractId: payload.refineryContractId,
    referenceNumber: payload.referenceNumber || '',
    declaredValue: isNil(payload.declaredValue) ? null : parseFloat(payload.declaredValue),
    projectedGrossWeight: payload.projectedGrossWeight,
    expectedPurity: (payload.expectedPurity === '' || payload.expectedPurity === undefined || payload.expectedPurity === null)
      ? 0
      : payload.expectedPurity,
    paymentMethodId: payload.paymentMethodId,
    advanceEnabled: payload.advanceEnabled,
    trackingNumbers: payload.trackingNumbers || null,
    lotDescriptionIds: payload.lotDescriptionIds,
    autoAcceptCashAdvance: payload.autoAcceptCashAdvance || false,
    notes: payload.notes || '',
    cashAdvanceValue: payload.cashAdvanceValue,
    cashAdvanceMetalWeight: payload.cashAdvanceMetalWeight,
    snapshotSpotPriceGold: payload.snapshotSpotPriceGold,
    snapshotSpotPriceSilver: payload.snapshotSpotPriceSilver,
    snapshotSpotPricePlatinum: payload.snapshotSpotPricePlatinum,
    snapshotSpotPricePalladium: payload.snapshotSpotPricePalladium,
    spotLocksToFulfill: payload.spotLocksToFulfill,
    newSpotLocksRequired: payload.newSpotLocksRequired
  }
}

const getDefaultState = () => ({
  lotDescriptions: [],
  selectedRefiningLot: null,
  refiningLotLoading: false,
  refineryContractsForLot: [],
  lots: {
    data: {
      count: 0,
      rows: [],
    },
    totals: {}
  },
})

const state = getDefaultState()

const getters = {
  getLotDescriptions: state => state.lotDescriptions.sort((a, b) => a.id - b.id),
  getSelectedRefiningLot: state => state.selectedRefiningLot,
  getRefiningLotLoading: state => state.refiningLotLoading,
  getRefineryContractsForLot: state => state.refineryContractsForLot || [],
  getRefiningLots: state => state.lots,
  getRefiningLotsAdmin: state => state.lots?.data?.rows || [],
  getRefiningLotsLoading: state => state.refiningLotLoading,
  hasFullAssayView: state => Boolean(state.lots?.data?.can?.fullAssayView),
}

const actions = {
  async fetchLotDescriptions({commit}) {
    const response = await request.get('/refinery/lot-descriptions')
    commit('SET_LOT_DESCRIPTIONS', response)
    return response
  },

  async fetchRefineryContractsForLot({commit, getters}) {
    try {
      const companyId = validateCompanySelected(getters)
      const response = await request.get(`/refinery-contracts/${companyId}`)
      const contracts = response?.data?.rows || response || []
      commit('SET_REFINERY_CONTRACTS_FOR_LOT', contracts)
      return contracts
    } catch (error) {
      commit('SET_REFINERY_CONTRACTS_FOR_LOT', [])
      throw error
    }
  },

  async submitRefiningLot({getters}, payload) {
    const companyId = validateCompanySelected(getters)
    const mappedPayload = buildSubmissionPayload(companyId, payload)
    const {result} = await request.post(`/refinery/lots/${companyId}`, mappedPayload)
    return result
  },

  async fetchRefiningLotsAdmin({ commit }, { options = {}, query }) {
    try {
      // Provide defaults to prevent NaN calculations
      const page = options.page ?? 1
      const itemsPerPage = options.itemsPerPage ?? 50
      
      const params = {
        limit: itemsPerPage,
        offset: (page - 1) * itemsPerPage,
        // Convert sort arrays if provided
        sortBy: options.sortBy,
        sortDesc: options.sortDesc,
        ...query,
      }
      // Clean undefined params
      Object.keys(params).forEach((k) => params[k] === undefined && delete params[k])
      const result = await request.get('/refinery/lots', { params })
      const { data } = result;
      commit('SET_REFINING_LOTS', data)
    } catch (error) {
      console.error('Error fetching refining lots:', error)
      throw error
    }
  },

  async fetchRefiningLotsByCompany({ commit, getters }, { options = {}, query }) {
    try {
      // Provide defaults to prevent NaN calculations
      const page = options.page ?? 1
      const itemsPerPage = options.itemsPerPage ?? 50
      
      const params = {
        limit: itemsPerPage,
        offset: (page - 1) * itemsPerPage,
        // Convert sort arrays if provided
        sortBy: options.sortBy,
        sortDesc: options.sortDesc,
        ...query,
      }
      const companyId = getters['getSelectedCompany']?.id;
      // Clean undefined params
      Object.keys(params).forEach((k) => params[k] === undefined && delete params[k])
      const { data } = await request.get(`/refinery/lots/${companyId}`, { params })
      commit('SET_REFINING_LOTS', data)
    } catch (error) {
      console.error('Error fetching refining lots:', error)
      throw error
    }
  },

  async fetchRefiningLot({getters}, lotId) {
    const companyId = validateCompanySelected(getters)
    validateLotId(lotId)
    return request.get(`/refinery/lots/${companyId}/${lotId}`)
  },

  async updateRefiningLot({getters}, {id, ...payload}) {
    const companyId = validateCompanySelected(getters)
    validateLotId(id)
    return request.patch(`/refinery/lots/${companyId}/${id}`, payload)
  },

  async fetchSelectedRefiningLot({commit, getters}, {id}) {
    commit('SET_REFINING_LOT_LOADING', true)
    try {
      const companyId = validateCompanySelected(getters)
      const lot = await request.get(`/refinery/lots/${companyId}/${id}`)
      const normalizedLot = normalizeRichWeights(lot)
      commit('SET_SELECTED_REFINING_LOT', normalizedLot)
      return normalizedLot
    } catch (error) {
      commit('SET_SELECTED_REFINING_LOT', null)
      throw error
    } finally {
      commit('SET_REFINING_LOT_LOADING', false)
    }
  },

  clearSelectedRefiningLot({commit}) {
    commit('SET_SELECTED_REFINING_LOT', null)
  },

  async appendTrackingNumbers({getters}, {lotId, trackingNumbers}) {
    const companyId = validateCompanySelected(getters)
    validateLotIdParam(lotId)

    // try to use selected lot if it matches
    let lot = getters['getSelectedRefiningLot']
    if (!lot || lot.id !== lotId) {
      lot = await request.get(`/refinery/lots/${companyId}/${lotId}`)
    }
    const lotCode = lot.lotCode
    if (!lotCode) throw new Error(ERROR_MESSAGES.LOT_CODE_MISSING)

    await request.post(`/refinery/lots/${companyId}/${lotCode}/tracking-numbers`, {trackingNumbers})
  },

  async markRefiningLotReceived(_, {companyId, lotId, trackingNumbers, receivedGrossWeight, receiptNotes}) {
    if (!companyId) throw new Error(ERROR_MESSAGES.COMPANY_ID_REQUIRED)
    if (!lotId) throw new Error(ERROR_MESSAGES.LOT_ID_PARAM_REQUIRED)
    const payload = {}
    // Note: trackingNumbers are now fetched from database by backend, not sent from frontend
    if (receivedGrossWeight !== undefined) {
      payload.receivedGrossWeight = receivedGrossWeight
    }
    if (receiptNotes !== undefined) {
      payload.receiptNotes = receiptNotes
    }
    return request.post(`/refinery/lots/${companyId}/${lotId}/receive`, payload)
  },

  async recordLotIntakes(_, {companyId, records}) {
    if (!companyId) throw new Error(ERROR_MESSAGES.COMPANY_ID_REQUIRED)
    if (!records || !Array.isArray(records) || records.length === 0) {
      throw new Error(ERROR_MESSAGES.RECORDS_ARRAY_REQUIRED)
    }
    return request.post(`/refinery/lot-intakes/${companyId}`, {records})
  },

/**
   * Fetch available FedEx/ShipSecure shipment rate quotes for a refining lot
   * @param {Object} context - Vuex action context
   * @param {Object} params - Request parameters
   * @param {string} params.companyId - Company ID (falls back to selected company if not provided)
   * @param {string} params.lotId - Refining lot ID
   * @param {Object} params.data - Rate request data (pickupType, packagingType, packages, declaredValue, etc.)
   * @returns {Promise<Object>} Rate quotes with surcharge calculation
   */
  async listRefiningLotShipmentQuotes({getters}, {companyId, lotId, data}) {
    const resolvedCompanyId = companyId || validateCompanySelected(getters)
    validateLotId(lotId)
    return request.post(`/refinery/lots/${resolvedCompanyId}/${lotId}/shipment/rates`, data)
  },

  /**
   * Validate a FedEx/ShipSecure shipment for a refining lot (fast pre-flight check)
   * @param {Object} context - Vuex action context
   * @param {Object} params - Request parameters
   * @param {string} params.companyId - Company ID (falls back to selected company if not provided)
   * @param {string} params.lotId - Refining lot ID
   * @param {Object} params.data - Shipment validation data (serviceType, pickupType, packages, etc.)
   * @returns {Promise<Object>} Validation result from FedEx
   */
  async validateRefiningLotShipment({getters}, {companyId, lotId, data}) {
    const resolvedCompanyId = companyId || validateCompanySelected(getters)
    validateLotId(lotId)
    return request.post(`/refinery/lots/${resolvedCompanyId}/${lotId}/shipment/validate`, data)
  },

  /**
   * Create a FedEx/ShipSecure shipment for a refining lot
   * @param {Object} context - Vuex action context
   * @param {Object} params - Request parameters
   * @param {string} params.companyId - Company ID (falls back to selected company if not provided)
   * @param {string} params.lotId - Refining lot ID
   * @param {Object} params.data - Shipment creation data (serviceType, pickupType, packages, etc.)
   * @returns {Promise<Object>} Created shipment with FedEx response data
   */
  async createRefiningLotShipment({getters, commit}, {companyId, lotId, data, operationMeta = {}}) {
    const resolvedCompanyId = companyId || validateCompanySelected(getters)
    validateLotId(lotId)
    const operationId = `create-${lotId}-${Date.now()}`
    commit('ADD_SHIPSECURE_PENDING_OPERATION', {
      id: operationId, type: 'create', entityType: 'refiningLot',
      entityId: lotId,
      ...operationMeta,
    })
    try {
      return await request.post(`/refinery/lots/${resolvedCompanyId}/${lotId}/shipment`, data)
    } finally {
      commit('REMOVE_SHIPSECURE_PENDING_OPERATION', operationId)
    }
  },

  /**
   * Cancel a FedEx/ShipSecure shipment for a refining lot
   * @param {Object} context - Vuex action context
   * @param {Object} params - Request parameters
   * @param {string} params.companyId - Company ID (falls back to selected company if not provided)
   * @param {string} params.lotId - Refining lot ID
   * @param {string} params.shipmentId - Shipment ID to cancel
   * @returns {Promise<Object>} Cancelled shipment
   */
  async cancelRefiningLotShipment({getters, commit}, {companyId, lotId, shipmentId, operationMeta = {}}) {
    const resolvedCompanyId = companyId || validateCompanySelected(getters)
    validateLotId(lotId)
    const operationId = `delete-${shipmentId}-${Date.now()}`
    commit('ADD_SHIPSECURE_PENDING_OPERATION', {
      id: operationId, type: 'delete', entityType: 'refiningLot',
      entityId: lotId, shipmentId,
      ...operationMeta,
    })
    try {
      return await request.delete(`/refinery/lots/${resolvedCompanyId}/${lotId}/shipment/${shipmentId}`)
    } finally {
      commit('REMOVE_SHIPSECURE_PENDING_OPERATION', operationId)
    }
  },

  async emailRefiningLot({getters}, {companyId, id, email}) {
    // Use provided companyId or fall back to selected company
    const resolvedCompanyId = companyId || validateCompanySelected(getters)
    validateLotId(id)
    
    if (!email) throw new Error('Email address is required')
    
    return request.post(`/refinery/lots/${resolvedCompanyId}/${id}/email`, {
      email,
      display: '0' // Send actual email, not just return HTML
    })
  },

  async createRefiningLotSettlement({getters}, {companyId, refiningLotId, settlementOffer}) {
    // Use provided companyId or fall back to selected company
    const resolvedCompanyId = companyId || validateCompanySelected(getters)
    validateLotId(refiningLotId)
    
    if (!settlementOffer) {
      throw new Error('Settlement offer is required')
    }
    
    if (!settlementOffer.metalData || Object.keys(settlementOffer.metalData).length === 0) {
      throw new Error('Metal data is required')
    }
    
    if (!settlementOffer.feeCalculations) {
      throw new Error('Fee calculations are required')
    }
    
    const {result} = await request.post(`/refinery/lots/${resolvedCompanyId}/${refiningLotId}/settlement`, {
      settlementOffer
    })
    return result
  },

  async approveRefiningLotSettlement({getters}, {companyId, refiningLotId, ...payload}) {
    // Use provided companyId or fall back to selected company
    const resolvedCompanyId = companyId || validateCompanySelected(getters)
    validateLotId(refiningLotId)
    
    // Basic validation - let backend handle detailed validation
    if (!payload.settlementOption) {
      throw new Error('Settlement option is required')
    }
    
    // Pass through the entire payload to the backend
    const {result} = await request.post(`/refinery/lots/${resolvedCompanyId}/${refiningLotId}/settlement/approve`, payload)
    return result
  },

  async deleteRefiningLotSettlement({getters}, {companyId, refiningLotId}) {
    // Use provided companyId or fall back to selected company
    const resolvedCompanyId = companyId || validateCompanySelected(getters)
    validateLotId(refiningLotId)
    
    const {result} = await request.delete(`/refinery/lots/${resolvedCompanyId}/${refiningLotId}/settlement`)
    return result
  },

  async createCashAdvanceForLot({getters}, {companyId, refiningLotId, snapshotData, cashAdvanceValue, cashAdvanceMetalWeight, spotLocksToFulfill, newSpotLocksRequired}) {
    const resolvedCompanyId = companyId || validateCompanySelected(getters)
    validateLotId(refiningLotId)

    const payload = { ...(snapshotData || {}) }
    if (cashAdvanceValue !== undefined) payload.cashAdvanceValue = cashAdvanceValue
    if (cashAdvanceMetalWeight !== undefined) payload.cashAdvanceMetalWeight = cashAdvanceMetalWeight
    if (spotLocksToFulfill !== undefined) payload.spotLocksToFulfill = spotLocksToFulfill
    if (newSpotLocksRequired !== undefined) payload.newSpotLocksRequired = newSpotLocksRequired

    const {result} = await request.post(`/refinery/lots/${resolvedCompanyId}/${refiningLotId}/create-advance`, payload)
    return result
  },

  async updateLotAssayExclusion({getters}, {lotAssayId, isExcluded, exclusionReason}) {
    const companyId = validateCompanySelected(getters)
    if (!lotAssayId) throw new Error('lotAssayId required')
    
    const payload = { isExcluded }
    if (exclusionReason !== undefined) {
      payload.exclusionReason = exclusionReason
    }
    
    return request.patch(`/refinery/lot-assays/${companyId}/${lotAssayId}`, payload)
  },

  async fetchAssayDeletionImpact({getters}, {lotAssayId}) {
    const companyId = validateCompanySelected(getters)
    if (!lotAssayId) throw new Error('lotAssayId required')
    
    return request.get(`/refinery/lot-assays/${companyId}/${lotAssayId}/deletion-impact`)
  },

  async deleteLotAssay({getters}, {lotAssayId, deletionReason}) {
    const companyId = validateCompanySelected(getters)
    if (!lotAssayId) throw new Error('lotAssayId required')
    if (!deletionReason) throw new Error('deletionReason required')
    
    const response = await request.delete(`/refinery/lot-assays/${companyId}/${lotAssayId}`, {
      data: { deletionReason }
    })
    
    return response.result
  },

  async fetchDeletedAssays({getters}, {refiningLotId}) {
    const companyId = validateCompanySelected(getters)
    if (!refiningLotId) throw new Error('refiningLotId required')
    
    return request.get(`/refinery/lots/${companyId}/${refiningLotId}/deleted-assays`)
  },

  async restoreLotAssay({getters}, {lotAssayId}) {
    const companyId = validateCompanySelected(getters)
    if (!lotAssayId) throw new Error('lotAssayId required')
    
    const response = await request.post(`/refinery/lot-assays/${companyId}/${lotAssayId}/restore`)
    
    return response.result
  },

  async downloadRefiningLot({dispatch}, {companyId, id, filename}) {
    if (!companyId) throw new Error('Company ID is required')
    if (!id) throw new Error('Refining lot ID is required')
    if (!filename) throw new Error('Filename is required')
    
    const url = `/refinery/lots/${companyId}/${id}/pdf`
    return dispatch('_downloadFile', {filename, url})
  },

  async htmlRefiningLot({getters}, {companyId, id}) {
    // Use provided companyId or fall back to selected company
    const resolvedCompanyId = companyId || validateCompanySelected(getters)
    validateLotId(id)
    
    return request({
      url: `/refinery/lots/${resolvedCompanyId}/${id}/html`,
      method: 'get',
    })
  },

  async requestDeclaredWeightUpdate({getters}, {companyId, refiningLotId, ...payload}) {
    const resolvedCompanyId = companyId || validateCompanySelected(getters)
    validateLotId(refiningLotId)
    return request.post(`/refinery/lots/${resolvedCompanyId}/${refiningLotId}/declared-weight/request`, payload)
  },

  async approveDeclaredWeightUpdate({getters}, {companyId, refiningLotId, ...payload}) {
    const resolvedCompanyId = companyId || validateCompanySelected(getters)
    validateLotId(refiningLotId)
    return request.post(`/refinery/lots/${resolvedCompanyId}/${refiningLotId}/declared-weight/approve`, payload)
  },
}

const mutations = {
  SET_LOT_DESCRIPTIONS(state, descriptions) {
    state.lotDescriptions = descriptions
  },
  SET_SELECTED_REFINING_LOT(state, lot) {
    state.selectedRefiningLot = lot
  },
  SET_REFINING_LOT_LOADING(state, loading) {
    state.refiningLotLoading = loading
  },
  SET_REFINERY_CONTRACTS_FOR_LOT(state, contracts) {
    state.refineryContractsForLot = contracts
  },
  SET_REFINING_LOTS(state, data) {
    state.lots.data = data
  },
}

export default {
  state,
  getters,
  mutations,
  actions,
}

